import { useMemo, useState } from "react"
import { DateTime } from "luxon"
import { Grid } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { MenuPlaningQuery, MenusListResponse, fetchMenusList } from "../menuPlanningApi"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { SelectWeekAutocomplete } from "../components/SelectWeekAutocomplete"
import { MenuDetails } from "../components/MenuDetails"
import { generateWeeks } from "../components/helpers"
import { DailyMenu } from "../menuPlanningTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { DetailedMenu } from "../components/DetailedMenu"
import {
    DefaultPreviewProductImageStateType,
    PreviewProductImage,
    defaultPreviewProductImageState,
} from "../components/PreviewProductImage"
import { StandardDateFilter } from "../../../../../shared/components/table/StandardDateFilter"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { DateFilterParams, useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { StandardDateSwitcherButton } from "../../../../../shared/components/switcher/StandardDateSwitcherButton"
import { useMenuPlanningItemActions } from "../components/useMenuPlanningItemActions"

const selectMenus = (data: MenusListResponse): DailyMenu[] => {
    const menuPlanning = data?.menuPlanning
    if (!menuPlanning || !menuPlanning.weeks) {
        return []
    }

    // Process each menu and its dishes into list

    return menuPlanning.weeks.flatMap((week) => week.dailyMenus).flatMap((menu) => menu)
}

export function Menus() {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const defaultStartDate = DateTime.now().startOf("week")
    const defaultEndDate = DateTime.now().endOf("week")

    const [isDatePicker, setIsDatePicker] = useState<boolean>(false)
    const { startDate, endDate } = useInitialDateFilters(defaultStartDate, defaultEndDate)
    const [startDateState, setStartDate] = useState<DateTime>(startDate)
    const [endDateState, setEndDate] = useState<DateTime>(endDate)
    const weeks = useMemo(() => generateWeeks(startDate), [startDate.toISODate()])

    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )

    const menuPlanningResult = useApiQuery(fetchMenusList, {
        queryName: MenuPlaningQuery.LIST,
        dependencies: {
            merchantId,
            from: startDateState.toISODate(),
            to: endDateState.toISODate(),
        },
        select: selectMenus,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(menuPlanningResult)

    const columns = useMemo<MRT_ColumnDef<DailyMenu>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-menu-planning-table-date-column"),
                accessorKey: "publishDate",
                Cell: ({ row }) => {
                    return <MenuDetails menu={row.original} />
                },
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("merchant-menu-planning-table-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    const handleDateView = () => {
        setIsDatePicker((prev) => !prev)
        setStartDate(startDate)
        setEndDate(endDate)
    }

    const [getActions, dialogs] = useMenuPlanningItemActions()

    return (
        <>
            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderDetailPanel={({ row }) => {
                        return <DetailedMenu dishes={row.original.dishes} onProductPhotoClick={setPreviewImage} />
                    }}
                    renderRowActionMenuItems={({ row, closeMenu }) => getActions(row.original, closeMenu)}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            {isDatePicker ? (
                                <>
                                    <LocalizedStrict
                                        id="merchant-menu-planning-table-startDate-date-picker"
                                        attrs={{ label: true }}
                                    >
                                        <StandardDateFilter
                                            value={startDateState}
                                            onChange={(newValue) => {
                                                if (newValue) {
                                                    setStartDate(newValue)
                                                }
                                            }}
                                            maxDate={endDateState}
                                            label="Start date"
                                            paramName={DateFilterParams.START}
                                        />
                                    </LocalizedStrict>

                                    <LocalizedStrict
                                        id="merchant-menu-planning-table-endDate-date-picker"
                                        attrs={{ label: true }}
                                    >
                                        <StandardDateFilter
                                            value={endDateState}
                                            onChange={(newValue) => {
                                                if (newValue) {
                                                    setEndDate(newValue)
                                                }
                                            }}
                                            label="End date"
                                            minDate={startDateState}
                                            paramName={DateFilterParams.END}
                                        />
                                    </LocalizedStrict>
                                </>
                            ) : (
                                <SelectWeekAutocomplete
                                    weeks={weeks}
                                    startDate={startDateState}
                                    endDate={endDateState}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                />
                            )}
                            <StandardDateSwitcherButton handleChange={handleDateView} />
                        </StandardTableFilter>
                    )}
                />
            </Grid>
            {dialogs}
            {/* Product preview */}
            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}
        </>
    )
}
